import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './ActivityTracker.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaChartBar,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaUserGraduate,
  FaBookMedical,
  FaLayerGroup,
  FaCalendarDay,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaSearch,
  FaTimes
} from 'react-icons/fa';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getDayName = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'long' });
};

const getMonthName = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const ActivityTracker = ({ user, assignedCourse, myStudentIds, allStudents }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [allEntries, setAllEntries] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedTopics, setExpandedTopics] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');

  const validBatches = (() => {
    const minStartYear = new Date().getFullYear() - 4;
    return [...new Set((allStudents || []).map(s => s.batch).filter(Boolean))]
      .filter(b => {
        const startYear = parseInt(b.split('-')[0]);
        return !isNaN(startYear) && startYear >= minStartYear;
      })
      .sort();
  })();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchData = useCallback(async () => {
    if (!assignedCourse) return;

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const [entriesRes, courseRes] = await Promise.all([
        fetch(
          `${collegeConfig.api.baseURL}/logbook/entries/course/${assignedCourse._id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        ),
        fetch(
          `${collegeConfig.api.baseURL}/courses/${assignedCourse._id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        )
      ]);

      if (entriesRes.ok) {
        const entriesData = await entriesRes.json();
        setAllEntries(entriesData.entries || []);
      } else {
        throw new Error('Failed to fetch entries');
      }

      if (courseRes.ok) {
        const courseData = await courseRes.json();
        setModules(courseData.course?.modules || []);
      }
    } catch (err) {
      console.error('Error fetching activity data:', err);
      setError('Failed to load activity data');
    } finally {
      setLoading(false);
    }
  }, [assignedCourse]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build studentId → batch map from allStudents
  const studentBatchMap = useMemo(() => {
    const map = {};
    (allStudents || []).forEach(s => {
      map[String(s._id || s.id)] = s.batch || '';
    });
    return map;
  }, [allStudents]);

  // Filter entries by myStudentIds, selectedMonth, and selectedBatch
  const filteredEntries = useMemo(() => {
    if (!selectedBatch) return [];

    return allEntries.filter(entry => {
      const studentId = String(entry.studentId?._id || entry.studentId || '');

      // Filter by selected students
      if (myStudentIds && myStudentIds.length > 0) {
        if (!myStudentIds.includes(studentId)) return false;
      }

      // Filter by batch using the map
      if (studentBatchMap[studentId] !== selectedBatch) return false;

      // Filter by month
      const entryDate = new Date(entry.metadata?.submittedAt || entry.createdAt);
      const entryMonth = entryDate.toISOString().slice(0, 7);
      if (entryMonth !== selectedMonth) return false;

      // Only show submitted/approved entries (not drafts)
      if (entry.status === 'draft') return false;

      return true;
    });
  }, [allEntries, myStudentIds, selectedMonth, selectedBatch, studentBatchMap]);

  // Get module info by moduleId
  const getModuleInfo = useCallback((moduleId) => {
    const mod = modules.find(m =>
      (m.moduleId || m._id)?.toString() === moduleId?.toString()
    );
    return mod || null;
  }, [modules]);

  // Extract topic/grouping key from entry data
  const getTopicKey = useCallback((entry, moduleInfo) => {
    if (entry.entryType === 'bulk') {
      return 'Counter Progress';
    }

    if (!entry.entries || entry.entries.length === 0) return 'No Data';

    const fields = moduleInfo?.fields || [];
    const firstEntry = entry.entries[0];
    const data = firstEntry?.data;

    if (!data) return 'No Data';

    // Find the first text/select/textarea field to use as topic
    const topicField = fields.find(f =>
      f.type === 'text' || f.type === 'select' || f.type === 'textarea'
    );

    if (topicField) {
      const value = data instanceof Map ? data.get(topicField.key) : data[topicField.key];
      if (value && String(value).trim()) {
        return String(value).trim();
      }
    }

    // Fallback: use first non-date field value
    for (const field of fields) {
      if (field.type === 'date') continue;
      const value = data instanceof Map ? data.get(field.key) : data[field.key];
      if (value && String(value).trim()) {
        return String(value).trim();
      }
    }

    return 'Untitled Entry';
  }, []);

  // Get all field values for display
  const getEntryDetails = useCallback((entry, moduleInfo) => {
    const fields = moduleInfo?.fields || [];
    const details = [];

    if (entry.entryType === 'bulk' && entry.counters) {
      const counters = entry.counters instanceof Map
        ? Object.fromEntries(entry.counters)
        : entry.counters;

      fields.forEach(field => {
        const value = counters[field.key];
        if (value !== undefined) {
          details.push({ label: field.label || field.key, value: String(value), type: 'counter' });
        }
      });
      return details;
    }

    if (!entry.entries || entry.entries.length === 0) return details;

    const firstEntry = entry.entries[0];
    const data = firstEntry?.data;
    if (!data) return details;

    fields.forEach(field => {
      const value = data instanceof Map ? data.get(field.key) : data[field.key];
      if (value !== undefined && value !== null && String(value).trim()) {
        let displayValue = String(value);
        if (field.type === 'date' && value) {
          displayValue = formatDate(value);
        }
        details.push({ label: field.label || field.key, value: displayValue, type: field.type });
      }
    });

    return details;
  }, []);

  // Build grouped data structure
  const groupedData = useMemo(() => {
    const groups = {};

    filteredEntries.forEach(entry => {
      const entryDate = new Date(entry.metadata?.submittedAt || entry.createdAt);
      const dateKey = entryDate.toISOString().slice(0, 10);
      const moduleInfo = getModuleInfo(entry.moduleId);
      const moduleName = moduleInfo?.name || 'Unknown Module';
      const topicKey = getTopicKey(entry, moduleInfo);

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: entryDate,
          dateKey,
          totalEntries: 0,
          studentIds: new Set(),
          modules: {}
        };
      }

      const dayGroup = groups[dateKey];
      dayGroup.totalEntries++;
      const studentId = entry.studentId?._id || entry.studentId;
      dayGroup.studentIds.add(studentId);

      if (!dayGroup.modules[moduleName]) {
        dayGroup.modules[moduleName] = {
          moduleName,
          moduleInfo,
          totalEntries: 0,
          topics: {}
        };
      }

      const moduleGroup = dayGroup.modules[moduleName];
      moduleGroup.totalEntries++;

      if (!moduleGroup.topics[topicKey]) {
        moduleGroup.topics[topicKey] = {
          topicKey,
          students: [],
          count: 0,
          details: getEntryDetails(entry, moduleInfo)
        };
      }

      const topicGroup = moduleGroup.topics[topicKey];
      topicGroup.count++;
      topicGroup.students.push({
        name: `${entry.studentId?.profile?.firstName || ''} ${entry.studentId?.profile?.lastName || ''}`.trim(),
        email: entry.studentId?.email || '',
        studentId,
        entryId: entry._id,
        status: entry.status,
        entryCount: entry.entries?.length || 0
      });
    });

    // Convert studentIds Sets to counts and sort dates descending
    const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
    const result = {};
    sortedDates.forEach(dateKey => {
      const day = groups[dateKey];
      result[dateKey] = {
        ...day,
        uniqueStudents: day.studentIds.size
      };
    });

    return result;
  }, [filteredEntries, getModuleInfo, getTopicKey, getEntryDetails]);

  // Summary stats
  const stats = useMemo(() => {
    const allStudentIds = new Set();
    const allModuleNames = new Set();
    let totalEntries = 0;

    Object.values(groupedData).forEach(day => {
      totalEntries += day.totalEntries;
      day.studentIds.forEach(id => allStudentIds.add(id));
      Object.keys(day.modules).forEach(name => allModuleNames.add(name));
    });

    return {
      totalEntries,
      activeStudents: allStudentIds.size,
      activityTypes: allModuleNames.size,
      daysWithActivity: Object.keys(groupedData).length
    };
  }, [groupedData]);

  // Filtered dates by search
  const filteredDates = useMemo(() => {
    if (!searchTerm) return Object.keys(groupedData);

    const search = searchTerm.toLowerCase();
    return Object.keys(groupedData).filter(dateKey => {
      const day = groupedData[dateKey];
      // Search in module names and topics
      return Object.values(day.modules).some(mod =>
        mod.moduleName.toLowerCase().includes(search) ||
        Object.keys(mod.topics).some(topic => topic.toLowerCase().includes(search)) ||
        Object.values(mod.topics).some(topic =>
          topic.students.some(s =>
            s.name.toLowerCase().includes(search) ||
            s.email.toLowerCase().includes(search)
          )
        )
      );
    });
  }, [groupedData, searchTerm]);

  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const monthPickerRef = React.useRef(null);

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const newDate = new Date(year, month - 1 + direction);
    const newYear = newDate.getFullYear();
    const newMonth = String(newDate.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${newYear}-${newMonth}`);
    setExpandedDays({});
    setExpandedModules({});
    setExpandedTopics({});
  };

  const selectMonth = (monthIndex) => {
    const newMonth = String(monthIndex + 1).padStart(2, '0');
    setSelectedMonth(`${pickerYear}-${newMonth}`);
    setShowMonthPicker(false);
    setExpandedDays({});
    setExpandedModules({});
    setExpandedTopics({});
  };

  // Close month picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target)) {
        setShowMonthPicker(false);
      }
    };
    if (showMonthPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMonthPicker]);

  const toggleDay = (dateKey) => {
    setExpandedDays(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  const toggleModule = (key) => {
    setExpandedModules(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTopic = (key) => {
    setExpandedTopics(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="at-status-badge at-status-verified"><FaCheckCircle /> Verified</span>;
      case 'submitted':
      case 'under_review':
        return <span className="at-status-badge at-status-pending"><FaClock /> Pending</span>;
      case 'needs_revision':
        return <span className="at-status-badge at-status-revision"><FaExclamationTriangle /> Revision</span>;
      default:
        return <span className="at-status-badge at-status-pending"><FaClock /> {status}</span>;
    }
  };

  const getModuleColor = (moduleName) => {
    const colors = ['#3498DB', '#E67E22', '#2ECC71', '#9B59B6', '#E74C3C', '#1ABC9C', '#F39C12', '#34495E'];
    let hash = 0;
    for (let i = 0; i < moduleName.length; i++) {
      hash = moduleName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <div className="at-container">
        <div className="at-loading">
          <div className="spinner"></div>
          <p>Loading activity data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="at-container">
      {/* Header */}
      <div className="at-header">
        <div className="at-title-section">
          <h2><FaChartBar style={{ marginRight: '10px' }} /> Activity Tracker</h2>
          <p className="at-subtitle">
            Consolidated view of student activities for your selected students
          </p>
        </div>
      </div>

      {error && <div className="at-error">{error}</div>}

      {/* Batch Selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <label style={{ fontWeight: 600, color: '#444', whiteSpace: 'nowrap' }}>Batch:</label>
        <select
          value={selectedBatch}
          onChange={(e) => setSelectedBatch(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, cursor: 'pointer' }}
        >
          <option value="">Select Batch</option>
          {validBatches.map(b => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>

      {!selectedBatch && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888', background: '#f8f9fa', borderRadius: 10, marginBottom: 16 }}>
          Please select a batch to view activity data.
        </div>
      )}

      {selectedBatch && <>
      {/* Month Navigator */}
      <div className="at-month-nav" ref={monthPickerRef}>
        <button className="at-month-btn" onClick={() => navigateMonth(-1)}>
          <FaChevronLeft />
        </button>
        <div
          className="at-month-label at-month-label-clickable"
          onClick={() => {
            const [year] = selectedMonth.split('-').map(Number);
            setPickerYear(year);
            setShowMonthPicker(!showMonthPicker);
          }}
        >
          <FaCalendarAlt style={{ marginRight: '8px' }} />
          {getMonthName(selectedMonth)}
          <FaChevronDown style={{ marginLeft: '6px', fontSize: '12px' }} />
        </div>
        <button className="at-month-btn" onClick={() => navigateMonth(1)}>
          <FaChevronRight />
        </button>

        {showMonthPicker && (
          <div className="at-month-picker">
            <div className="at-picker-year-nav">
              <button className="at-picker-arrow" onClick={() => setPickerYear(y => y - 1)}>
                <FaChevronLeft />
              </button>
              <span className="at-picker-year">{pickerYear}</span>
              <button className="at-picker-arrow" onClick={() => setPickerYear(y => y + 1)}>
                <FaChevronRight />
              </button>
            </div>
            <div className="at-picker-months">
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => {
                const isSelected = selectedMonth === `${pickerYear}-${String(i + 1).padStart(2, '0')}`;
                return (
                  <button
                    key={m}
                    className={`at-picker-month ${isSelected ? 'selected' : ''}`}
                    onClick={() => selectMonth(i)}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="at-stats-grid">
        <div className="at-stat-card">
          <div className="at-stat-icon" style={{ background: '#3498DB' }}>
            <FaBookMedical />
          </div>
          <div className="at-stat-content">
            <h3>{stats.totalEntries}</h3>
            <p>Total Entries</p>
          </div>
        </div>
        <div className="at-stat-card">
          <div className="at-stat-icon" style={{ background: '#2ECC71' }}>
            <FaUserGraduate />
          </div>
          <div className="at-stat-content">
            <h3>{stats.activeStudents}</h3>
            <p>Active Students</p>
          </div>
        </div>
        <div className="at-stat-card">
          <div className="at-stat-icon" style={{ background: '#E67E22' }}>
            <FaLayerGroup />
          </div>
          <div className="at-stat-content">
            <h3>{stats.activityTypes}</h3>
            <p>Activity Types</p>
          </div>
        </div>
        <div className="at-stat-card">
          <div className="at-stat-icon" style={{ background: '#9B59B6' }}>
            <FaCalendarDay />
          </div>
          <div className="at-stat-content">
            <h3>{stats.daysWithActivity}</h3>
            <p>Active Days</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="at-search-bar">
        <FaSearch className="at-search-icon" />
        <input
          type="text"
          placeholder="Search activities, topics, or students..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="at-search-input"
        />
        {searchTerm && (
          <button className="at-search-clear" onClick={() => setSearchTerm('')}>
            <FaTimes />
          </button>
        )}
      </div>

      {/* Timeline */}
      {filteredDates.length === 0 ? (
        <div className="at-empty">
          <FaCalendarAlt className="at-empty-icon" />
          <h3>No Activities Found</h3>
          <p>
            {searchTerm
              ? `No activities match "${searchTerm}" for ${getMonthName(selectedMonth)}.`
              : `No logbook entries found for ${getMonthName(selectedMonth)}.`}
          </p>
        </div>
      ) : (
        <div className="at-timeline">
          {filteredDates.map(dateKey => {
            const day = groupedData[dateKey];
            const isExpanded = expandedDays[dateKey];
            const moduleNames = Object.keys(day.modules);

            return (
              <div key={dateKey} className={`at-day-card ${isExpanded ? 'expanded' : ''}`}>
                {/* Day Header */}
                <div className="at-day-header" onClick={() => toggleDay(dateKey)}>
                  <div className="at-day-date">
                    <span className="at-day-number">{new Date(dateKey).getDate()}</span>
                    <div className="at-day-info">
                      <span className="at-day-name">{getDayName(dateKey)}</span>
                      <span className="at-day-full-date">{formatDate(dateKey)}</span>
                    </div>
                  </div>
                  <div className="at-day-meta">
                    <div className="at-day-dots">
                      {moduleNames.slice(0, 5).map((name, i) => (
                        <span
                          key={i}
                          className="at-module-dot"
                          style={{ background: getModuleColor(name) }}
                          title={name}
                        />
                      ))}
                    </div>
                    <span className="at-day-badge">{day.totalEntries} {day.totalEntries === 1 ? 'entry' : 'entries'}</span>
                    <span className="at-day-students">{day.uniqueStudents} {day.uniqueStudents === 1 ? 'student' : 'students'}</span>
                    <span className="at-expand-icon">
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </div>
                </div>

                {/* Expanded Day Content */}
                {isExpanded && (
                  <div className="at-day-content">
                    {moduleNames.map(moduleName => {
                      const mod = day.modules[moduleName];
                      const moduleKey = `${dateKey}-${moduleName}`;
                      const isModuleExpanded = expandedModules[moduleKey] !== false; // Default expanded
                      const color = getModuleColor(moduleName);
                      const topicKeys = Object.keys(mod.topics);

                      return (
                        <div key={moduleKey} className="at-module-section">
                          <div
                            className="at-module-header"
                            onClick={() => toggleModule(moduleKey)}
                            style={{ borderLeftColor: color }}
                          >
                            <div className="at-module-title">
                              <span className="at-module-color-dot" style={{ background: color }} />
                              <h4>{moduleName}</h4>
                              <span className="at-module-count">{mod.totalEntries}</span>
                            </div>
                            <span className="at-expand-icon">
                              {isModuleExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </span>
                          </div>

                          {isModuleExpanded && (
                            <div className="at-module-content">
                              {topicKeys.map(topicKey => {
                                const topic = mod.topics[topicKey];
                                const topicId = `${moduleKey}-${topicKey}`;
                                const isTopicExpanded = expandedTopics[topicId];

                                return (
                                  <div key={topicId} className="at-topic-card">
                                    <div
                                      className="at-topic-header"
                                      onClick={() => toggleTopic(topicId)}
                                    >
                                      <div className="at-topic-info">
                                        <h5 className="at-topic-name">{topicKey}</h5>
                                        <span className="at-topic-student-count">
                                          {topic.count} {topic.count === 1 ? 'student' : 'students'}
                                        </span>
                                      </div>
                                      <span className="at-expand-icon-small">
                                        {isTopicExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                      </span>
                                    </div>

                                    {/* Student Chips (always visible) */}
                                    <div className="at-student-chips">
                                      {topic.students.map((student, idx) => (
                                        <div key={idx} className="at-student-chip">
                                          <span className="at-chip-avatar">
                                            {student.name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
                                          </span>
                                          <span className="at-chip-name">{student.name}</span>
                                          {getStatusBadge(student.status)}
                                        </div>
                                      ))}
                                    </div>

                                    {/* Expanded Topic Details */}
                                    {isTopicExpanded && topic.details.length > 0 && (
                                      <div className="at-topic-details">
                                        <div className="at-details-grid">
                                          {topic.details.map((detail, idx) => (
                                            <div key={idx} className="at-detail-item">
                                              <span className="at-detail-label">{detail.label}</span>
                                              <span className="at-detail-value">{detail.value}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>}
    </div>
  );
};

export default ActivityTracker;